/**
 * Level 11 — the Endless City.
 *
 * Grid-aligned roads, concrete sidewalks, asphalt with yellow lines on it, and
 * an overcast sky that never gets round to becoming either noon or evening. The
 * wiki is specific that the architecture is "bland and unassuming" and that
 * about a third of the buildings are "indestructible and unenterable", with
 * black-tinted windows and no way in. It is also specific that the streets have
 * things wrong with them at the joints: buildings "strangely placed on top of
 * one another, floating, or even clipping inside each other".
 *
 * The streets are `Cell.Open`, so there is no ceiling over them and the renderer
 * carries the facades up past the roofline — the same trick Level 94 uses to
 * make a row of houses read as buildings. What is different here is what you can
 * see from the street: nothing but more street. Level 94 is a neighbourhood
 * arranged around a landmark, so being lost on it is a choice. This is the same
 * daylight with the landmark taken away.
 *
 * The culvert is the level's one vertical idea. Level 11's canals "go
 * underground and connect to sewers", and a roofed water channel cutting across
 * the block grid gives the city the one dark place in it.
 */

import { Cell } from '../../core/types';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Cells from one street's near kerb to the next. Sets the city block size. */
const BLOCK_PITCH: readonly [number, number] = [13, 19];
/** Street width in cells. */
const STREET: readonly [number, number] = [2, 3];
/** Fraction of blocks that are sealed: no door, no windows, no way in. */
const SEALED = 0.34;
/** Fraction of the rest that are open lots rather than buildings. */
const LOT = 0.2;
/** Blocks that push a corner out into the road. */
const INTRUSIONS: readonly [number, number] = [5, 10];

/** A street lane, as the span of cells it occupies. */
interface Lane {
  a: number;
  b: number;
}

/** Carve the Endless City profile into `site`. */
export function generateEndlessCity(site: GenSite): void {
  const { grid, rng, spec } = site;
  const bounds = interiorRect(grid);

  const cols = laneRun(site, bounds.x, bounds.x + bounds.w - 1);
  const rows = laneRun(site, bounds.y, bounds.y + bounds.h - 1);
  if (cols.length < 2 || rows.length < 2) return;

  for (const c of cols) {
    const r: Rect = { x: c.a, y: bounds.y, w: c.b - c.a + 1, h: bounds.h };
    fillRect(grid, r, Cell.Open);
    addRegion(site, r, 'hall');
  }
  for (const r0 of rows) {
    const r: Rect = { x: bounds.x, y: r0.a, w: bounds.w, h: r0.b - r0.a + 1 };
    fillRect(grid, r, Cell.Open);
    addRegion(site, r, 'hall');
  }

  // Before the blocks, not after: a corner pushed into the road must not be
  // able to land on the one street cell a building's only door opens onto, and
  // the door placement below checks what it is opening into.
  intrude(site, cols, rows, bounds, rng.int(INTRUSIONS[0], INTRUSIONS[1]));

  for (let j = 0; j + 1 < rows.length; j++) {
    for (let i = 0; i + 1 < cols.length; i++) {
      const block: Rect = {
        x: cols[i]!.b + 1,
        y: rows[j]!.b + 1,
        w: cols[i + 1]!.a - cols[i]!.b - 1,
        h: rows[j + 1]!.a - rows[j]!.b - 1,
      };
      if (block.w < 4 || block.h < 4) continue;
      const roll = rng.next();
      if (roll < SEALED) continue;
      if (roll < SEALED + LOT) carveLot(site, block);
      else carveBuilding(site, block);
    }
  }

  if (spec.waterDepth) carveCulvert(site, bounds, rows);

  // Only the operations that make sense outdoors and at this scale. A doorway
  // in a facade with brick behind it, a wall that grows off a building and
  // stops in the middle of the road, a column standing in an opening. No inner
  // rooms: a ring of rock inside a city block is not a strange room, it is a
  // building the player will never see the inside of.
  applyWrongness(site, {
    innerRooms: [0, 0],
    doorsToNothing: [14, 22],
    perforatedWalls: [1, 3],
    pillarsInDoorways: [2, 5],
    wallFins: [8, 14],
    tightPillarPatches: [1, 3],
    finLength: [3, 6],
    ringRooms: false,
  });
}

/** Street lanes across a span, with a city block between each pair. */
function laneRun(site: GenSite, from: number, to: number): Lane[] {
  const { rng } = site;
  const out: Lane[] = [];
  let t = from;
  while (t <= to) {
    const w = rng.int(STREET[0], STREET[1]);
    if (t + w - 1 > to) break;
    out.push({ a: t, b: t + w - 1 });
    t += w + rng.int(BLOCK_PITCH[0], BLOCK_PITCH[1]);
  }
  return out;
}

/**
 * An open building: a ground floor with a couple of internal walls and one or
 * two doors onto the street. The facade is the block's own outer ring, so a
 * building that is open and one that is not look identical until you have
 * walked round it.
 */
function carveBuilding(site: GenSite, block: Rect): void {
  const { grid, rng } = site;
  const inner = inset(block, 1);
  if (inner.w < 2 || inner.h < 2) return;
  fillRect(grid, inner, Cell.Floor);

  // Internal walls. Each runs from one side and stops short of the other, so a
  // ground floor is always one connected space however many go in — and a wall
  // that stops in the middle of a room is the register the level wants anyway.
  const walls = Math.max(1, Math.round((inner.w * inner.h) / 60));
  for (let i = 0; i < walls; i++) {
    const horizontal = rng.chance(0.5);
    const span = horizontal ? inner.w : inner.h;
    const len = Math.max(1, Math.round(span * rng.range(0.5, 0.8)));
    const fromStart = rng.chance(0.5);
    if (horizontal) {
      const y = rng.int(inner.y + 1, inner.y + inner.h - 2);
      const x0 = fromStart ? inner.x : inner.x + inner.w - len;
      for (let k = 0; k < len; k++) grid.set(x0 + k, y, Cell.Wall);
    } else {
      const x = rng.int(inner.x + 1, inner.x + inner.w - 2);
      const y0 = fromStart ? inner.y : inner.y + inner.h - len;
      for (let k = 0; k < len; k++) grid.set(x, y0 + k, Cell.Wall);
    }
  }

  punchStreetDoors(site, block, inner);
  addRegion(site, inner, inner.w * inner.h > 120 ? 'atrium' : 'room');
}

/**
 * One street door per part of the ground floor.
 *
 * The internal walls can leave a building as two or three separate units, and a
 * door punched at a random point of the facade lands against the back of one of
 * those walls about as often as not — which produced whole blocks the level
 * could only be joined to by `repairConnectivity` tunnelling in through the
 * brick. So the interior is component-labelled and each component is given its
 * own opening onto a cell that is actually a street. That is also how real
 * buildings on a block work: three units, three front doors.
 */
function punchStreetDoors(site: GenSite, block: Rect, inner: Rect): void {
  const { grid, rng } = site;
  const w = inner.w;
  const h = inner.h;
  const comp = new Int32Array(w * h).fill(-1);
  const stack: number[] = [];
  const DX = [1, -1, 0, 0];
  const DY = [0, 0, 1, -1];
  let parts = 0;

  for (let k = 0; k < w * h; k++) {
    if (comp[k] !== -1) continue;
    if (!grid.isWalkable(inner.x + (k % w), inner.y + ((k / w) | 0))) continue;
    comp[k] = parts;
    stack.push(k);
    while (stack.length > 0) {
      const cur = stack.pop()!;
      const cx = cur % w;
      const cy = (cur / w) | 0;
      for (let d = 0; d < 4; d++) {
        const nx = cx + DX[d]!;
        const ny = cy + DY[d]!;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (comp[j] !== -1) continue;
        if (!grid.isWalkable(inner.x + nx, inner.y + ny)) continue;
        comp[j] = parts;
        stack.push(j);
      }
    }
    parts++;
  }

  const x1 = block.x + block.w - 1;
  const y1 = block.y + block.h - 1;
  for (let part = 0; part < parts; part++) {
    const options: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < w; i++) {
      if (comp[i] === part && grid.isWalkable(inner.x + i, block.y - 1)) {
        options.push({ x: inner.x + i, y: block.y });
      }
      if (comp[(h - 1) * w + i] === part && grid.isWalkable(inner.x + i, y1 + 1)) {
        options.push({ x: inner.x + i, y: y1 });
      }
    }
    for (let j = 0; j < h; j++) {
      if (comp[j * w] === part && grid.isWalkable(block.x - 1, inner.y + j)) {
        options.push({ x: block.x, y: inner.y + j });
      }
      if (comp[j * w + w - 1] === part && grid.isWalkable(x1 + 1, inner.y + j)) {
        options.push({ x: x1, y: inner.y + j });
      }
    }
    if (options.length === 0) continue;
    const door = rng.pick(options);
    grid.set(door.x, door.y, Cell.Doorway);
  }
}

/**
 * A lot: the block with no building on it, open to the sky like the streets
 * around it. The wiki allows the city parks and nothing else wild, and a grid
 * cannot draw a park — so these are hard standing with a colonnade left on
 * them, which is what a city square looks like when nobody has put anything in
 * it.
 */
function carveLot(site: GenSite, block: Rect): void {
  const { grid, rng } = site;
  fillRect(grid, block, Cell.Open);
  // A kiosk or a stair head standing on it. Solid rather than a colonnade,
  // because a column only blocks the middle of its cell and `Cell.Pillar` needs
  // a floor with a ceiling over it to read as holding anything up.
  if (block.w >= 8 && block.h >= 8 && rng.chance(0.6)) {
    const w = rng.int(2, 4);
    const h = rng.int(2, 4);
    fillRect(
      grid,
      { x: block.x + rng.int(2, block.w - w - 2), y: block.y + rng.int(2, block.h - h - 2), w, h },
      Cell.Wall,
    );
  }
  addRegion(site, block, 'atrium');
}

/**
 * Buildings pushed out into the road.
 *
 * Never more than one cell of a street's width, so a road that was three wide
 * is still two wide where a corner is standing in it. The point is not to block
 * anything — it is that the kerb line stops being straight, which on a level
 * whose only navigational information is the street grid is the difference
 * between a junction you recognise and one you think you recognise.
 */
function intrude(site: GenSite, cols: readonly Lane[], rows: readonly Lane[], bounds: Rect, count: number): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    const vertical = rng.chance(0.5);
    const lanes = vertical ? cols : rows;
    const lane = lanes[rng.int(0, lanes.length - 1)]!;
    // Only the wide streets. Eating a cell off a two-cell road leaves a
    // single-file gap that two intrusions on opposite kerbs could close, and a
    // severed road is a repair scar rather than a corner sticking out.
    if (lane.b - lane.a + 1 < 3) continue;
    const edge = rng.chance(0.5) ? lane.a : lane.b;
    const len = rng.int(2, 6);
    const along = vertical ? bounds.y : bounds.x;
    const span = vertical ? bounds.h : bounds.w;
    const start = along + rng.int(0, Math.max(0, span - len));
    for (let k = 0; k < len; k++) {
      const t = start + k;
      if (vertical) grid.set(edge, t, Cell.Wall);
      else grid.set(t, edge, Cell.Wall);
    }
  }
}

/**
 * The culvert: a roofed water channel running the width of the level, offset
 * from the street grid so it cuts through the blocks rather than following one.
 *
 * `Cell.Water` carries a ceiling and `Cell.Open` does not, so the channel is
 * covered along its whole length including where it crosses a street — which is
 * exactly what a culvert under a road looks like from inside it. It is the only
 * dark place on the level and the only one with a roof you can touch.
 */
function carveCulvert(site: GenSite, bounds: Rect, rows: readonly Lane[]): void {
  const { grid, rng } = site;
  if (rows.length < 3) return;
  const between = rng.int(1, rows.length - 2);
  const above = rows[between]!.b;
  const below = rows[between + 1]!.a;
  if (below - above < 8) return;
  const y = above + Math.round((below - above) / 2);
  const r: Rect = { x: bounds.x, y: y - 1, w: bounds.w, h: 2 };
  fillRect(grid, r, Cell.Water);
  addRegion(site, r, 'flooded');
  // Steps down into it from the two streets it passes between, so the channel
  // is somewhere you can choose to go rather than something you fall into.
  for (let i = 0; i < 3; i++) {
    const x = bounds.x + rng.int(4, bounds.w - 5);
    for (let t = above + 1; t < y - 1; t++) if (grid.at(x, t) === Cell.Wall) grid.set(x, t, Cell.Floor);
    for (let t = y + 1; t < below; t++) if (grid.at(x, t) === Cell.Wall) grid.set(x, t, Cell.Floor);
  }
}
